import { createSlice } from '@reduxjs/toolkit';
import { QualityColorEnum } from '~/domain/enum/stockModalTabs.enum';
import * as M from '~/domain/model';
import * as tagPrintingApi from './apiCalls';

export type ProductProps = {
  id: number;
  origin: string;
  productId: number;
  productCode: string;
  productDescription: string;
  productQuality: QualityColorEnum;
  packingId: number;
  packingCode: string;
  packingName: string;
  packingQuantity: number;
  unitQuantity: number;
  quantity: number;
  observation: string;
  qualityObservation1: string;
  qualityObservation2: string;
  qualityObservationCode1: string;
  qualityObservationCode2: string;
  qualityObservationDesc1: string;
  qualityObservationDesc2: string;
  internalObservation: string;
  changed?: boolean;
};

export type TagsByCustomerProps = {
  customerId: number;
  customerCode: string;
  customerName: string;
  disposablePackingQuantity: number;
  returnablePackingQuantity: number;
  totalPacking: number;
  totalQuantity: number;
  products: ProductProps[];
};

export type CustomerProps = {
  error: boolean;
  id: number;
  origin: string;
  customerId: number;
  customerCode: string;
  customerName: string;
  packingId: number;
  packingCode: string;
  packingName: string;
  packingQuantity: number;
  unitQuantity: number;
  quantity: number;
  observation: string;
  qualityObservation1: string;
  qualityObservation2: string;
  qualityObservationCode1: string;
  qualityObservationCode2: string;
  qualityObservationDesc1: string;
  qualityObservationDesc2: string;
  internalObservation: string;
  changed?: boolean;
};

export type TagsByProductProps = {
  id: number;
  productId: number;
  productCode: string;
  productDescription: string;
  productQuality: string;
  disposablePackingQuantity: number;
  returnablePackingQuantity: number;
  totalPacking: number;
  totalQuantity: number;
  customers: CustomerProps[];
};

export type TagsByProduct = {
  tags: TagsByProductProps[];
  totalTags: number;
  pagination: M.DefaultPagination;
};

export type TagsByCustomer = {
  tags: TagsByCustomerProps[];
  totalTags: number;
  pagination: M.DefaultPagination;
};

export type OrderProps = {
  id: number;
  orderId: number;
  productCode: string;
  productDescription: string;
  customerCode: string;
  customerName: string;
  quality: string;
  quantityPacking: number;
  unitQuantity: number;
  quantity: number;
};

export type PrintedItemProps = {
  id: number;
  printedDate: string;
  quantity: number;
  userId: number;
  userName: string;
  siteId: number;
  siteCode: string;
  siteName: string;
  orders: OrderProps[];
};

export type PrintedListProps = {
  printedItems: PrintedItemProps[];
  totalPrintedItems: number;
  pagination: M.DefaultPagination;
};

export interface TagPrintingState {
  tagsByProduct: TagsByProduct;
  tagsByCustomer: TagsByCustomer;
  printedList: PrintedListProps;
  loading: boolean;
  errorIds: number[];
}

const initialState = {
  tagsByProduct: {
    tags: [],
    pagination: { PageNumber: 1, PageSize: 100 },
    totalTags: 0
  },
  tagsByCustomer: {
    tags: [],
    pagination: { PageNumber: 1, PageSize: 100 },
    totalTags: 0
  },
  printedList: {
    printedItems: [],
    pagination: { PageNumber: 1, PageSize: 10 },
    totalPrintedItems: 0
  },
  loading: false,
  errorIds: [] as number[]
};

const tagPrintingSlice = createSlice({
  name: 'tag-print',
  initialState,
  reducers: {
    clear: () => initialState,

    resetPrintModalState: state => {
      state.printedList = initialState.printedList;
    },

    setPrintedListPagination: (state, { payload }) => {
      state.printedList.pagination = {
        PageNumber: payload.PageNumber,
        PageSize: payload.PageSize
      };
    },

    setErrors: (state, action) => {
      state.errorIds = action.payload.errorIds;
    },

    removeError: (state, action) => {
      const { id } = action.payload;
      state.errorIds = state.errorIds.filter(errorId => errorId !== id);
    },

    setInternalObservation: (
      state,
      {
        payload
      }: {
        payload: {
          itemId: number;
          observation: string;
          rowId: number;
          viewByClient: boolean;
          changed: boolean;
        };
      }
    ) => {
      const { itemId, observation, rowId, viewByClient, changed } = payload;

      if (viewByClient) {
        const index = state.tagsByCustomer.tags.findIndex(
          (tag: TagsByCustomerProps) => tag.customerId === rowId
        );

        const expandendRowIndex = (
          state.tagsByCustomer.tags[index] as TagsByCustomerProps
        ).products.findIndex(c => c.id === itemId);

        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].internalObservation = observation;
        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].changed = changed;
      } else {
        const index = state.tagsByProduct.tags.findIndex(
          (tag: TagsByProductProps) => tag.id === rowId
        );

        const expandendRowIndex = (
          state.tagsByProduct.tags[index] as TagsByProductProps
        ).customers.findIndex(c => c.id === itemId);

        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].internalObservation = observation;
        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].changed = changed;
      }
    },
    setObservation1: (
      state,
      {
        payload
      }: {
        payload: {
          itemId: number;
          observation1: string;
          rowId: number;
          viewByClient: boolean;
          changed: boolean;
        };
      }
    ) => {
      const { itemId, observation1, rowId, viewByClient, changed } = payload;

      if (viewByClient) {
        const index = state.tagsByCustomer.tags.findIndex(
          (tag: TagsByCustomerProps) => tag.customerId === rowId
        );

        const expandendRowIndex = (
          state.tagsByCustomer.tags[index] as TagsByCustomerProps
        ).products.findIndex(c => c.id === itemId);

        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].qualityObservation1 = observation1;
        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].changed = changed;
      } else {
        const index = state.tagsByProduct.tags.findIndex(
          (tag: TagsByProductProps) => tag.id === rowId
        );

        const expandendRowIndex = (
          state.tagsByProduct.tags[index] as TagsByProductProps
        ).customers.findIndex(c => c.id === itemId);

        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].qualityObservation1 = observation1;
        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].changed = changed;
      }
    },
    setObservation2: (
      state,
      {
        payload
      }: {
        payload: {
          itemId: number;
          observation2: string;
          rowId: number;
          viewByClient: boolean;
          changed: boolean;
        };
      }
    ) => {
      const { itemId, observation2, rowId, viewByClient, changed } = payload;

      if (viewByClient) {
        const index = state.tagsByCustomer.tags.findIndex(
          (tag: TagsByCustomerProps) => tag.customerId === rowId
        );

        const expandendRowIndex = (
          state.tagsByCustomer.tags[index] as TagsByCustomerProps
        ).products.findIndex(c => c.id === itemId);

        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].qualityObservation2 = observation2;
        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].changed = changed;
      } else {
        const index = state.tagsByProduct.tags.findIndex(
          (tag: TagsByProductProps) => tag.id === rowId
        );

        const expandendRowIndex = (
          state.tagsByProduct.tags[index] as TagsByProductProps
        ).customers.findIndex(c => c.id === itemId);

        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].qualityObservation2 = observation2;
        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].changed = changed;
      }
    },
    setPackingId: (
      state,
      {
        payload
      }: {
        payload: {
          itemId: number;
          packingId: number;
          rowId: number;
          viewByClient: boolean;
          changed: boolean;
        };
      }
    ) => {
      const { itemId, packingId, rowId, viewByClient, changed } = payload;

      if (viewByClient) {
        const index = state.tagsByCustomer.tags.findIndex(
          (tag: TagsByCustomerProps) => tag.customerId === rowId
        );

        const expandendRowIndex = (
          state.tagsByCustomer.tags[index] as TagsByCustomerProps
        ).products.findIndex(c => c.id === itemId);

        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].packingId = packingId;
        (state.tagsByCustomer.tags[index] as TagsByCustomerProps).products[
          expandendRowIndex
        ].changed = changed;
      } else {
        const index = state.tagsByProduct.tags.findIndex(
          (tag: TagsByProductProps) => tag.id === rowId
        );

        const expandendRowIndex = (
          state.tagsByProduct.tags[index] as TagsByProductProps
        ).customers.findIndex(c => c.id === itemId);

        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].packingId = packingId;
        (state.tagsByProduct.tags[index] as TagsByProductProps).customers[
          expandendRowIndex
        ].changed = changed;
      }
    }
  },
  extraReducers: builder => {
    // Fetch tags by product view
    builder.addCase(tagPrintingApi.fetchTagsByProductView.pending, state => {
      state.loading = true;
      state.tagsByProduct.tags = [];
      state.tagsByProduct.totalTags = 0;
      state.tagsByCustomer.tags = [];
      state.tagsByCustomer.totalTags = 0;
    });
    builder.addCase(
      tagPrintingApi.fetchTagsByProductView.fulfilled,
      (state, { payload }) => {
        const { results, totalCount } = payload;
        state.loading = false;
        state.tagsByProduct.tags = results;
        state.tagsByProduct.totalTags = totalCount;
      }
    );
    builder.addCase(tagPrintingApi.fetchTagsByProductView.rejected, state => {
      state.loading = false;
    });

    builder.addCase(tagPrintingApi.fetchKlokTagsProductView.pending, state => {
      state.loading = true;
      state.tagsByProduct.tags = [];
      state.tagsByProduct.totalTags = 0;
      state.tagsByCustomer.tags = [];
      state.tagsByCustomer.totalTags = 0;
    });
    builder.addCase(
      tagPrintingApi.fetchKlokTagsProductView.fulfilled,
      (state, { payload }) => {
        const { results, totalCount } = payload;
        state.loading = false;
        state.tagsByProduct.tags = results;
        state.tagsByProduct.totalTags = totalCount;
      }
    );
    builder.addCase(tagPrintingApi.fetchKlokTagsProductView.rejected, state => {
      state.loading = false;
    });

    // Fetch tags by customer view
    builder.addCase(tagPrintingApi.fetchTagsByCustomerView.pending, state => {
      state.loading = true;
      state.tagsByProduct.tags = [];
      state.tagsByProduct.totalTags = 0;
      state.tagsByCustomer.tags = [];
      state.tagsByCustomer.totalTags = 0;
    });
    builder.addCase(
      tagPrintingApi.fetchTagsByCustomerView.fulfilled,
      (state, { payload }) => {
        const { results, totalCount } = payload;
        state.loading = false;
        state.tagsByCustomer.tags = results;
        state.tagsByCustomer.totalTags = totalCount;
      }
    );
    builder.addCase(tagPrintingApi.fetchTagsByCustomerView.rejected, state => {
      state.loading = false;
    });

    builder.addCase(tagPrintingApi.fetchKlokTagsCustomerView.pending, state => {
      state.loading = true;
      state.tagsByProduct.tags = [];
      state.tagsByProduct.totalTags = 0;
      state.tagsByCustomer.tags = [];
      state.tagsByCustomer.totalTags = 0;
    });
    builder.addCase(
      tagPrintingApi.fetchKlokTagsCustomerView.fulfilled,
      (state, { payload }) => {
        const { results, totalCount } = payload;
        state.loading = false;
        state.tagsByCustomer.tags = results;
        state.tagsByCustomer.totalTags = totalCount;
      }
    );
    builder.addCase(
      tagPrintingApi.fetchKlokTagsCustomerView.rejected,
      state => {
        state.loading = false;
      }
    );

    // Fetch Printed List
    builder.addCase(tagPrintingApi.fetchPrintedList.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      tagPrintingApi.fetchPrintedList.fulfilled,
      (state, { payload }) => {
        const { results, totalCount } = payload;
        state.loading = false;
        state.printedList.printedItems = results;
        state.printedList.totalPrintedItems = totalCount;
      }
    );
    builder.addCase(tagPrintingApi.fetchPrintedList.rejected, state => {
      state.loading = false;
    });

    // Update orders
    builder.addCase(tagPrintingApi.updateOrders.pending, state => {
      state.loading = true;
    });
    builder.addCase(tagPrintingApi.updateOrders.fulfilled, state => {
      state.loading = false;
    });
    builder.addCase(tagPrintingApi.updateOrders.rejected, state => {
      state.loading = false;
    });
  }
});

export const actions = tagPrintingSlice.actions;
export const reducer = tagPrintingSlice.reducer;
